import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const AliquotBarcodeAssignment = () => {
  const { state } = useLocation();
  const { sampleId: pSample, aliquotCount: pCount } = useParams();
  const navigate = useNavigate();

  const base = useMemo(() => {
    try {
      return state || JSON.parse(sessionStorage.getItem("aliquotFlow")) || {};
    } catch {
      return state || {};
    }
  }, [state]);

  const sampleId = base?.sampleId || pSample || "";
  const aliquotCount = Number(base?.aliquotCount ?? pCount ?? 0);

  const encounterId = base?.encounterId ?? null;
  const returnTo = base?.returnTo || BIOBANK_ROUTES.SAMPLE_PROCESSING;
  const sampleMeta = base?.sampleMeta ?? null;
  const detailsForm = base?.detailsForm ?? null;

  const invalid =
    !sampleId || !Number.isFinite(aliquotCount) || aliquotCount <= 0;

  const barcodes = useMemo(
    () =>
      invalid
        ? []
        : Array.from(
            { length: aliquotCount },
            (_, i) => `${sampleId}-${i + 1}`
          ),
    [sampleId, aliquotCount, invalid]
  );

  const [toast, setToast] = useState(null);

  const persistAndForward = (nextPath) => {
    const ctx = {
      encounterId,
      returnTo,
      sampleId,
      aliquotCount,
      barcodes,
      sampleMeta,
      detailsForm,
    };
    sessionStorage.setItem("aliquotFlow", JSON.stringify(ctx));
    navigate(nextPath, { state: ctx });
  };

  const handleBack = () => {
    const ctx = {
      encounterId,
      returnTo,
      sampleId,
      aliquotCount,
      barcodes,
      sampleMeta,
      detailsForm,
    };
    sessionStorage.setItem("aliquotFlow", JSON.stringify(ctx));
    navigate(BIOBANK_SUB_ROUTES.SAMPLE_PROCESSING_PROCESS(encodeURIComponent(sampleId)), { state: ctx });
  };

  const handleContinue = () => {
    if (!barcodes.length) {
      setToast({ type: "warning", message: "No barcodes to continue." });
      return;
    }
    persistAndForward(BIOBANK_SUB_ROUTES.SAMPLE_PROCESSING_STORAGE_ASSIGNMENT(encodeURIComponent(sampleId)));
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ type: "success", message: "Copied to clipboard" });
    } catch {
      setToast({ type: "error", message: "Copy failed" });
    }
  };

  const handleCopyAll = () => {
    if (!barcodes.length) return;
    copyText(barcodes.join("\n"));
  };

  return (
    <FocusedFlowPage
      title="Aliquot Barcode Assignment"
      subtitle="Please navigate to the Bartender application and generate barcodes based on the IDs provided below. Once generated, print and attach the barcodes to the aliquots."
      onClose={() => navigate(returnTo)}
      maxWidth={760}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: "#8C5A3C",
            color: "#8C5A3C",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { borderColor: "#8C5A3C", background: "#FBF6F3" },
          }}
        >
          Back
        </Button>,
        <Button
          key="continue"
          variant="contained"
          onClick={handleContinue}
          endIcon={<ArrowForwardIcon />}
          disabled={invalid || barcodes.length === 0}
          sx={{
            bgcolor: "#500000",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { bgcolor: "#6a0000" },
          }}
        >
          Continue
        </Button>,
      ]}
    >
      <Snackbar
        open={!!toast}
        autoHideDuration={2000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.type || "success"}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>

        {invalid ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Missing sampleId or aliquotCount.
          </Alert>
        ) : barcodes.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No barcodes generated for sample <b>{sampleId}</b>.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Tooltip title="Copy all barcodes">
                <Button
                  onClick={handleCopyAll}
                  startIcon={<ContentCopyIcon />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "#8C5A3C",
                    color: "#8C5A3C",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#8C5A3C",
                      background: "#FBF6F3",
                    },
                  }}
                >
                  Copy All
                </Button>
              </Tooltip>
            </Box>

            <List dense sx={{ mb: 3 }}>
              {barcodes.map((code, idx) => (
                <ListItem
                  key={code}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.75,
                  }}
                  secondaryAction={
                    <Tooltip title="Copy barcode">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => copyText(code)}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Typography sx={{ color: "#1B8B2E", fontWeight: 600, mr: 1 }}>
                    {`Aliquot ${idx + 1} Barcode ID:`}
                  </Typography>
                  <Typography variant="body2">{code}</Typography>
                </ListItem>
              ))}
            </List>
          </>
        )}
    </FocusedFlowPage>
  );
};

export default AliquotBarcodeAssignment;
